// Category auto-fill from job title suggestion
//
// When the user picks a job title from the suggestions dropdown
// (see title_suggestions.ts), prefill the Category select with the
// category of that curated title suggestion (sourced from a static
// curated list, not historic job_ads, to avoid leaking past job titles).
// If the user then edits the
// title freely after picking, clear the auto-filled category so the
// (now-orphan) metadata doesn't get persisted alongside a different
// title — matches the LinkedIn-style behaviour where post-pick edits
// decouple typeahead-derived metadata.
//
// Per the AC: if the user types a new title without picking from the
// list, the category remains untouched (the
// `lj:title-suggestion-selected` CustomEvent only fires on pick, so
// nothing auto-fills in the first place).
//
// Manual category choices are preserved: we only clear on title edit
// when the select still holds the value we just auto-filled.

interface SuggestionDetail {
  title: string;
  category_id: number | null;
}

type SelectizedSelect = HTMLSelectElement & {
  selectize?: {
    setValue: (value: string | number, silent?: boolean) => void;
    clear: (silent?: boolean) => void;
    options: Record<string, unknown>;
  };
};

const initialize = () => {
  const titleInput = document.getElementById('job-title') as HTMLInputElement | null;
  const categorySelect = document.getElementById(
    'job_ad_form_category_id',
  ) as SelectizedSelect | null;

  if (!titleInput || !categorySelect) return;

  // Avoid double-binding when `turbolinks:load` fires twice on cached visits.
  if (titleInput.dataset.categoryAutofillBound === 'true') return;
  titleInput.dataset.categoryAutofillBound = 'true';

  // Tracks the category_id and the title we last auto-filled so that we
  // can both (a) skip clearing if the user has since changed the category
  // by hand, and (b) only clear when the title actually diverges from the
  // suggestion that triggered the auto-fill.
  let autofilledCategoryId: string | null = null;
  let autofilledForTitle: string | null = null;

  const setCategory = (value: string) => {
    if (categorySelect.selectize) {
      categorySelect.selectize.setValue(value, false);
    } else {
      categorySelect.value = value;
      categorySelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const clearCategory = () => {
    if (categorySelect.selectize) {
      categorySelect.selectize.clear(false);
    } else {
      categorySelect.value = '';
      categorySelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  titleInput.addEventListener('lj:title-suggestion-selected', ((
    event: CustomEvent<SuggestionDetail>,
  ) => {
    const categoryId = event.detail && event.detail.category_id;
    if (categoryId === null || categoryId === undefined) return;

    // Bail out if that category isn't a valid option (e.g. deactivated
    // since the curated YAML was last regenerated).
    //
    // Once selectize enhances the select it moves the <option> elements
    // into its own registry and leaves the native `.options` holding only
    // the (empty) selected one, so we must look the value up in selectize
    // when it's active — otherwise the lookup always fails and the
    // category never auto-fills.
    const stringValue = String(categoryId);
    const optionExists = categorySelect.selectize
      ? Object.prototype.hasOwnProperty.call(categorySelect.selectize.options, stringValue)
      : Array.from(categorySelect.options).some(option => option.value === stringValue);
    if (!optionExists) return;

    setCategory(stringValue);
    autofilledCategoryId = stringValue;
    autofilledForTitle = event.detail.title;
  }) as EventListener);

  // Title edits after a pick: if the title text has diverged from the
  // suggestion we auto-filled for AND the category still holds that
  // auto-fill, clear it. A manual category change in between sets
  // categorySelect.value away from autofilledCategoryId, so the check
  // naturally skips the clear.
  titleInput.addEventListener('input', () => {
    if (autofilledCategoryId === null) return;
    if (titleInput.value === autofilledForTitle) return;

    if (categorySelect.value === autofilledCategoryId) {
      clearCategory();
    }

    autofilledCategoryId = null;
    autofilledForTitle = null;
  });
};

// The post wizard is Turbolinks-driven, so we must NOT rely on
// `DOMContentLoaded` alone — it doesn't fire on Turbolinks visits and the
// listener wouldn't bind when arriving at step 1 via a cached visit.
document.addEventListener('turbolinks:load', initialize);
